import { useState, useEffect } from 'preact/hooks';
import { getEffectiveRealtimeEndDate } from './dateRange';
import { api } from './api';
import { appendDashboardFiltersToSearchParams, dashboardFilters, dateRangePreset, isDashboardFilterActive, selectedProperty, startDate, endDate, toggleDashboardFilter } from './state';

type PagesProps = {
    liveRefreshTick?: number;
};

const Pages = ({ liveRefreshTick = 0 }: PagesProps) => {
    const [pagesData, setPagesData] = useState<{ page: string; visits: number }[]>([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [dialogData, setDialogData] = useState<{ page: string; visits: number }[]>([]);
    const [totalVisits, setTotalVisits] = useState(0);

    const loadPages = async (options?: { limit?: number; showLoading?: boolean; updateDialog?: boolean }) => {
        if (!selectedProperty.value) {
            return;
        }

        const { limit, showLoading = true, updateDialog = false } = options ?? {};
        const effectiveEndDate = getEffectiveRealtimeEndDate(endDate.value);
        const params = new URLSearchParams({
            property: selectedProperty.value,
            start: startDate.value,
            end: effectiveEndDate,
        });
        appendDashboardFiltersToSearchParams(params);

        if (typeof limit === 'number') {
            params.set('limit', String(limit));
        }

        if (showLoading) {
            setLoading(true);
        }

        const result = await api(`/pages?${params.toString()}`);

        if (updateDialog) {
            setDialogData(result.data);
            return;
        }

        setPagesData(result.data);
        setTotalVisits(result.totalVisits);
        setLoading(false);
    };

    useEffect(() => {
        if (selectedProperty.value) {
            loadPages({ showLoading: true })
                .catch(() => {
                    setLoading(false);
                });
        }
    }, [selectedProperty.value, startDate.value, endDate.value, JSON.stringify(dashboardFilters.value)]);

    useEffect(() => {
        if (!selectedProperty.value) {
            return;
        }

        if (dateRangePreset.value !== 'live' || liveRefreshTick === 0) {
            return;
        }

        loadPages({ showLoading: false }).catch((error) => {
            console.error('Error refreshing pages data:', error);
        });
    }, [liveRefreshTick, JSON.stringify(dashboardFilters.value)]);

    const openDialog = () => {
        setShowDialog(true);
        loadPages({ limit: 1000, showLoading: false, updateDialog: true })
            .catch((error) => {
                console.error('Error fetching full pages data:', error);
            });
    };

    const renderTable = (data: typeof pagesData, showProgress = false) => (
        <table className="data-table">
            <thead>
                <tr>
                    <th>Pagina</th>
                    <th className="table-cell-number">Visite</th>
                    <th className="table-cell-percentage">%</th>
                </tr>
            </thead>
            <tbody>
                {data.map((page) => {
                    const percentage = totalVisits > 0 ? ((page.visits / totalVisits) * 100) : 0;
                    const percentageStr = percentage.toFixed(1);
                    const style = showProgress ? { '--progress-width': `${percentage}%` } as any : undefined;
                    return (
                        <tr
                            key={page.page}
                            style={style}
                            className={`filterable-row ${isDashboardFilterActive('page', page.page) ? 'is-filter-active' : ''}`}
                            onClick={() => toggleDashboardFilter('page', page.page)}
                        >
                            <td className="table-cell-primary">
                                {page.page}
                            </td>
                            <td className="table-cell-number">{page.visits}</td>
                            <td className="table-cell-percentage">{percentageStr}%</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    return (
        <>
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">Pagine più visitate</h3>
                </div>
                {loading ? (
                    <div className="loading">
                        <div className="loading-spinner"></div>
                    </div>
                ) : pagesData.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">📄</div>
                        <p className="empty-state-text">Nessun dato disponibile</p>
                    </div>
                ) : (
                    <>
                        {renderTable(pagesData, true)}
                        <button className="details-button" onClick={openDialog}>
                            Visualizza tutti i dettagli
                        </button>
                    </>
                )}
            </div>

            {showDialog && (
                <div className="dialog-overlay" onClick={() => setShowDialog(false)}>
                    <div className="dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h2 className="dialog-title">Pagine più visitate - Dettagli completi</h2>
                            <button className="dialog-close" onClick={() => setShowDialog(false)}>×</button>
                        </div>
                        <div className="dialog-body">
                            {renderTable(dialogData, true)}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Pages;
